import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { BnNgIdleService } from 'bn-ng-idle';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SearchModule } from './components/search/search.module';
import { QuickViewModule } from './components/quick-view/quick-view.module';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule } from '@angular/material/tree';
import { MaterialsModule } from './modules/material.module';
import { MatDialogModule } from '@angular/material/dialog';
import {
  DxListModule,
  DxLoadIndicatorModule,
  DxScrollViewModule,
  DxSelectBoxModule,
  DxTemplateModule,
  DxTextBoxModule,
} from 'devextreme-angular';
import { OrgSelectionModule } from './components/org-selection/org-selection.module';
import { MemoModule } from './components/memo/memo.module';
import { SlideOutModule } from './components/slide-out/slide-out.module';
import { TagModule } from './components/tag/tag.module';
import { OrgMultiselectionModule } from './components/org-multiselection/org-multiselection.module';
import { ListFilterModule } from './components/list-filter/list-filter.module';
import { TagFilterModule } from './components/tag-filter/tag-filter.module';
import { SearchBarModule } from './components/search-bar/search-bar.module';
import { InputBoxModule } from './components/input-box/input-box.module';
import { CmPageHeaderComponent } from './components/page-header/cm-page-header.component';
import { SecurityService } from '@app/shared/services/security/security.service';
import { FiltersModalModule } from '@shared/components/modal/filters-modal/filters-modal.module';
import { MatRippleModule } from '@angular/material/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { FilterModule } from './components/filter/filter.module';
import { BasicMetricsCardComponent } from './components/basic-metrics-card/basic-metrics-card.component';
import { FloatingButtonModule } from './components/floating-button/floating-button.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SortMenuModule } from './components/sort-menu/sort-menu.module';
import { CheckboxGroupModule } from './components/checkbox-group/checkbox-group.module';
import { InputSearchBoxModule } from './components/input-seach-box/input-search-box.module';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { EmptyListModule } from './components/empty-list/empty-list.module';
import { EmptySearchResultComponent } from './components/empty-search-result/empty-search-result.component';
import { InputQuantityModule } from './components/input-quantity/input-quantity.module';
import { MobileNopermissionsPopoverComponent } from './components/mobile-nopermissions-popover/mobile-nopermissions-popover.component';
import { PopoverModule } from './components/popover/popover.module';
import { FileUploadModule } from './components/file-upload/file-upload.module';
import { GenericDialogModule } from './components/generic-dialog/generic-dialog.module';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { ButtonsModule } from './components/buttons/buttons.module';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { SnackBarComponent } from './components/snackbar/snackbar.component';
import { DateFormatPipe } from '@shared/pipes/date-format.pipe';
import { DirectiveModule } from './directives/directive.module';
import { NoDataToShowComponent } from './components/no-data-to-show/no-data-to-show.component';
import { httpRequestInterceptor } from './services/http/http-interceptor';
import { ConfigurationService } from './services/utils/configuration.service';
import { BasicButtonComponent } from './components/buttons/basic-button/basic-button.component';
import { TextButtonWithIconComponent } from './components/buttons/text-button-with-icon/text-button-with-icon.component';
import { PermissionsPipe } from './pipes/permissions/permissions.pipe';
import { SlideToggleModule } from './components/slide-toggle/slide-toggle.module';
import { VersionCheckService } from './services/version-check/version-check.service';
import { HighlightPipe } from './pipes/highlight.pipe';
import { SkeletonsModule } from './components/skeletons/skeletons.module';
import { NarrativeEditDialogComponent } from './components/narrative-edit-dialog/narrative-edit-dialog.component';

const COMMON_MODULES = [
  DragDropModule,
  FloatingButtonModule,
  CommonModule,
  FormsModule,
  MatFormFieldModule,
  MatInputModule,
  HttpClientModule,
  ReactiveFormsModule,
  RouterModule,
  SearchModule,
  QuickViewModule,
  MatIconModule,
  MatTreeModule,
  MaterialsModule,
  MatDialogModule,
  TranslateModule,
  DxSelectBoxModule,
  DxLoadIndicatorModule,
  DxTemplateModule,
  OrgSelectionModule,
  OrgMultiselectionModule,
  MemoModule,
  SlideOutModule,
  FiltersModalModule,
  TagModule,
  ListFilterModule,
  TagFilterModule,
  SearchBarModule,
  InputBoxModule,
  MatRippleModule,
  MatBottomSheetModule,
  DxScrollViewModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxListModule,
  FilterModule,
  SortMenuModule,
  CheckboxGroupModule,
  InputSearchBoxModule,
  MatSnackBarModule,
  EmptyListModule,
  InputQuantityModule,
  FileUploadModule,
  GenericDialogModule,
  DirectiveModule,
  SlideToggleModule,
];


@NgModule({
  declarations: [
    CmPageHeaderComponent,
    BasicMetricsCardComponent,
    EmptySearchResultComponent,
    NoDataToShowComponent,
    MobileNopermissionsPopoverComponent,
    SideNavComponent,
    BreadcrumbComponent,
    SnackBarComponent,
    DateFormatPipe,
    PermissionsPipe,
    HighlightPipe,
    NarrativeEditDialogComponent,
  ],
  imports: [...COMMON_MODULES, PopoverModule, ButtonsModule],
  exports: [
    ...COMMON_MODULES,
    CmPageHeaderComponent,
    BasicMetricsCardComponent,
    EmptySearchResultComponent,
    NoDataToShowComponent,
    MobileNopermissionsPopoverComponent,
    SideNavComponent,
    BreadcrumbComponent,
    SnackBarComponent,
    DateFormatPipe,
    BasicButtonComponent,
    TextButtonWithIconComponent,
    PermissionsPipe,
    HighlightPipe,
    SkeletonsModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: httpRequestInterceptor,
      deps: [HttpClient, ConfigurationService, SecurityService],
      multi: true,
    },
    VersionCheckService,
  ],
})
export class SharedModule {
  static forRoot(): any {
    return {
      ngModule: SharedModule,
      providers: [BnNgIdleService],
    };
  }
}

export const httpLoaderFactory = (http: HttpClient) => new TranslateHttpLoader(http);
